import { useMemo } from "react";
import { ArrowLeft, Calendar, User } from "lucide-react";
import dayjs from "dayjs";

import { ZButton } from "@/shared/components/button";
import {
  ZModal,
  ZModalBody,
  ZModalContent,
} from "@/shared/components/modal";
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";
import { useGetTeamsListQuery } from "@/modules/auth/requests";
import type { ExistingAssessment } from "./types";

interface AssessmentExistsModalProps {
  isOpen: boolean;
  assessment: ExistingAssessment;
  memberHasAccess: boolean;
  onClose: () => void;
  onBack: () => void;
  onViewAssessment: () => void;
  onStartNew: () => void;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "—";
  return dayjs(dateString).format("DD MMM YYYY");
}

export function AssessmentExistsModal({
  isOpen,
  assessment,
  memberHasAccess,
  onClose,
  onBack,
  onViewAssessment,
  onStartNew,
}: AssessmentExistsModalProps) {
  // Fetch team members for name resolution
  const { data: teamsData } = useGetTeamsListQuery({ isEnabled: isOpen });

  const ownerId = assessment.permissions?.owner;

  const userName = useMemo(() => {
    if (!ownerId) return "Unknown";
    if (teamsData?.memberIdNameMapping?.[ownerId]) {
      return teamsData.memberIdNameMapping[ownerId];
    }
    return "Unknown";
  }, [ownerId, teamsData?.memberIdNameMapping]);

  const vendorData = assessment.multi_file_vendor_assessment;

  const displayName = useMemo(() => {
    let name = vendorData?.vendor_name || assessment.name || "Unknown";
    if (vendorData?.product_name) {
      name += ` ${vendorData.product_name}`;
    }
    return name;
  }, [vendorData?.vendor_name, vendorData?.product_name, assessment.name]);

  const vendorLogoUrl = vendorData?.vendor_logo_url || "";

  const progressPercent = useMemo(() => {
    if (!assessment.total_assessment_steps) return 0;
    return (assessment.assessment_step / assessment.total_assessment_steps) * 100;
  }, [assessment.assessment_step, assessment.total_assessment_steps]);

  return (
    <ZModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      isDismissable={true}
      aria-label="Assessment Exists"
    >
      <ZModalContent
        className="!my-0 relative"
        style={{
          width: "512px",
          maxWidth: "512px",
          borderRadius: "16px",
          boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.05)",
        }}
      >
        <ZModalBody className="flex flex-col items-center p-6 gap-6">
          {/* Container - Header + Subtitle with 8px gap */}
          <div className="flex flex-col items-center gap-2 w-full">
            {/* Header row - Back button + Title on same row */}
            <div
              className="flex flex-row items-start gap-2 w-full"
              style={{ height: "28px" }}
            >
              <button
                onClick={onBack}
                className="hover:opacity-70 transition-opacity py-1 flex-shrink-0"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-[#0F172B]" strokeWidth={1.5} />
              </button>
              <h2
                className="font-semibold text-[#0F172B] flex-1 text-center"
                style={{ fontSize: "18px", lineHeight: "28px" }}
              >
                An assessment for this third-party already exists
              </h2>
            </div>

            {/* Subtitle - centered */}
            <p
              className="text-center text-[#62748E] w-full"
              style={{ fontSize: "14px", lineHeight: "20px" }}
            >
              {memberHasAccess
                ? "Starting a new assessment will archive the existing one."
                : "Request access from your organization admin or the assessment owner to view or manage this assessment."}
            </p>
          </div>

          {/* Assessment Card */}
          <div
            className="flex flex-row items-start p-4 gap-4 w-full rounded-lg"
            style={{ backgroundColor: "#F8FAFC" }}
          >
            {/* Vendor Logo */}
            <VendorLogo
              src={vendorLogoUrl}
              alt={displayName}
              size={36}
              radius="full"
              needsAuthToken={!!vendorLogoUrl}
              className="border border-[#E2E8F0] flex-shrink-0"
            />

            {/* Assessment Info */}
            <div className="flex flex-col gap-3 flex-1 min-w-0">
              {/* Vendor Name */}
              <span
                className="font-semibold text-[#314158] truncate"
                style={{ fontSize: "16px", lineHeight: "24px" }}
              >
                {displayName}
              </span>

              {/* Progress Bar */}
              <div
                className="w-full rounded-lg overflow-hidden"
                style={{ height: "4px", backgroundColor: "#E2E8F0" }}
              >
                <div
                  className="h-full rounded-lg"
                  style={{
                    width: `${progressPercent}%`,
                    backgroundColor: "#006FEE",
                  }}
                />
              </div>

              {/* Meta Row */}
              <div className="flex items-center gap-6">
                {/* Date */}
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3 h-3 text-[#62748E]" />
                  <span
                    className="text-[#62748E]"
                    style={{ fontSize: "12px", lineHeight: "16px" }}
                  >
                    Updated {formatDate(assessment.updated_at)}
                  </span>
                </div>
                {/* User */}
                <div className="flex items-center gap-1.5">
                  <User className="w-3 h-3 text-[#62748E]" />
                  <span
                    className="text-[#62748E]"
                    style={{ fontSize: "12px", lineHeight: "16px" }}
                  >
                    {userName}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          {memberHasAccess && (
            <div className="flex flex-row gap-4 w-full" style={{ height: "40px" }}>
              <ZButton
                variant="bordered"
                className="flex-1"
                style={{
                  height: "40px",
                  padding: "8px 16px",
                  borderColor: "#006FEE",
                  color: "#006FEE",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: "24px",
                }}
                onPress={onViewAssessment}
              >
                View Assessment
              </ZButton>
              <ZButton
                color="primary"
                className="flex-1"
                style={{
                  height: "40px",
                  padding: "8px 16px",
                  backgroundColor: "#006FEE",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: "24px",
                }}
                onPress={onStartNew}
              >
                Start New Assessment
              </ZButton>
            </div>
          )}
        </ZModalBody>
      </ZModalContent>
    </ZModal>
  );
}
